/**
 * Hell/Dunkel zur Laufzeit (REQ-20 §6).
 *
 * Das Inline-Skript in `index.html` hat `data-theme` beim Laden bereits gesetzt
 * (REQ-19 §6). Der Hook liest deshalb den Ist-Zustand aus dem DOM und schreibt
 * nur bei einer echten Änderung — beim ersten Rendern darf nichts umgeschaltet
 * werden, sonst blitzt die andere Darstellung auf.
 */

import { useCallback, useState } from 'react';

import { THEME_STORAGE_KEY } from '@/config/appConfig';

export type Theme = 'light' | 'dark';

export interface ThemeState {
  theme: Theme;
  /** Eigenschaft statt Methode: Der Rückgabewert wird destrukturiert. */
  setTheme: (value: Theme) => void;
}

/** Ist-Zustand am `<html>`-Element; ohne Attribut gilt die helle Darstellung. */
function currentTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

export function useTheme(): ThemeState {
  const [theme, setState] = useState<Theme>(currentTheme);

  const setTheme = useCallback((value: Theme): void => {
    if (currentTheme() === value) {
      return;
    }

    document.documentElement.dataset.theme = value;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, value);
    } catch {
      // Kein localStorage (privater Modus) — die Umschaltung wirkt nur für diese Sitzung.
    }
    setState(value);
  }, []);

  return { theme, setTheme };
}
